import { urlBuildQueryParams } from 'pf-frontend-common/src/helper/url/build-query-params';
import { BrowserStorageLocalServiceInterface } from 'pf-frontend-common/src/service/browser-storage-local/service.interface';
import { WindowServiceInterface } from 'pf-frontend-common/src/service/window/service.interface';

import { CountryCodeEnum } from 'common/data/country/code.enum';
import { EmailAlertAdditionalParamsAdapter } from 'common/data/email-alert/additional-params-adapter';
import { LeadModelInterface } from 'common/data/lead/model.interface';
import { PropertyModel } from 'common/data/property/model';
import { UserModel } from 'common/data/user/model';
import { configGetCountry } from 'common/helper/config/get-country';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { objectFilterFalsy } from 'common/helper/object/filter/falsy';
import { InputValidatorEmail } from 'common/module/input/validator/email';
import { InputValidatorPhone } from 'common/module/input/validator/phone';
import { InputValidatorRequired } from 'common/module/input/validator/required';
import { LeadEmailFormContactInputFieldNameEnum } from 'common/module/lead/email/form/contact/input-field-name.enum';
import { LeadEmailFormInputInterface } from 'common/module/lead/email/form/input.interface';
import { LeadEmailFormStrategyInterface } from 'common/module/lead/email/form/strategy.interface';
import { LeadEmailFormStrategyAdapterInterface } from 'common/module/lead/email/form/strategy/adapter.interface';
import { LeadEmailFormStrategyStateInterface } from 'common/module/lead/email/form/strategy/state.interface';
import { LeadEmailFormStrategyStoreInterface } from 'common/module/lead/email/form/strategy/store.interface';
import { LeadEmailFormTypeEnum } from 'common/module/lead/email/form/type.enum';
import { LeadEmailFormOptionsInterface } from 'common/module/lead/email/form-options.interface';
import { EmailAlertStoreServiceInterface } from 'common/service/email-alert-store/service.interface';
import { LeadEmailFormContactServiceInterface } from 'common/service/lead-email-form-contact/service.interface';
import { UserAuthenticationServiceInterface } from 'common/service/user-authentication/service.interface';

import { LeadEmailFormStrategyStorageKeyEnum } from './storage-key.enum';

export class LeadEmailFormStrategyProperty implements LeadEmailFormStrategyInterface {
  /**
   * Local storage key
   */
  private localStorageKey: LeadEmailFormStrategyStorageKeyEnum = LeadEmailFormStrategyStorageKeyEnum.property;

  /**
   * Model
   */
  private model: PropertyModel;

  /**
   * Type of lead emai form
   */
  private formType: LeadEmailFormTypeEnum;

  /**
   * Constructor
   */
  constructor(
    public store: LeadEmailFormStrategyStoreInterface,
    public adapter: LeadEmailFormStrategyAdapterInterface,
    private windowService: WindowServiceInterface,
    private emailAlertService: EmailAlertStoreServiceInterface,
    private emailAlertAdditionalParamsAdapter: EmailAlertAdditionalParamsAdapter,
    private browserStorageLocalService: BrowserStorageLocalServiceInterface,
    private userAuthenticationService: UserAuthenticationServiceInterface,
    private leadEmailFormContactService: LeadEmailFormContactServiceInterface
  ) {}

  /**
   * @inheritDoc
   */
  public initialize(options: LeadEmailFormOptionsInterface): void {
    this.model = <PropertyModel>options.model;
    this.formType = options.type ? options.type : LeadEmailFormTypeEnum.default;

    this.setUserData();
  }

  /**
   * @inheritDoc
   */
  public getInitialState(): LeadEmailFormStrategyStateInterface {
    let message =
      this.formType === LeadEmailFormTypeEnum.liveViewing
        ? i18nTranslate(
            // tslint:disable-next-line:max-line-length
            'Hi, I joined the live viewing and I am interested in this property, Ref: #{ref} on Property Finder. Please contact me. Thank you.'
          )
        : i18nTranslate('Hi, I found your property with ref: #{ref} on Property Finder. Please contact me. Thank you.');

    if (this.model) {
      message = message.replace('#{ref}', this.model.reference);
    }

    const historyModel = <LeadModelInterface>this.browserStorageLocalService.getData(this.localStorageKey);

    const state: LeadEmailFormStrategyStateInterface = {
      input: {
        name: {
          value:
            this.leadEmailFormContactService.getData(LeadEmailFormContactInputFieldNameEnum.name) ||
            (historyModel ? historyModel.name : ''),
          placeholder: i18nTranslate('Your name'),
          validators: [new InputValidatorRequired(i18nTranslate('Please enter your name'))],
        },
        message: {
          value: message,
          placeholder: i18nTranslate('Your message'),
          validators: [new InputValidatorRequired(i18nTranslate('Please enter a message'))],
        },
        email: {
          value:
            this.leadEmailFormContactService.getData(LeadEmailFormContactInputFieldNameEnum.email) ||
            (historyModel ? historyModel.email : ''),
          placeholder: i18nTranslate('Your email'),
          validators: [
            new InputValidatorRequired(i18nTranslate('Please enter your email')),
            new InputValidatorEmail(i18nTranslate('Please enter a valid email')),
          ],
        },
        phone: {
          value:
            this.leadEmailFormContactService.getData(LeadEmailFormContactInputFieldNameEnum.phone) ||
            (historyModel ? historyModel.phone : ''),
          placeholder: i18nTranslate('Your phone'),
          validators: [
            new InputValidatorRequired(i18nTranslate('Please enter your phone number')),
            new InputValidatorPhone(i18nTranslate('Please enter a valid phone number')),
          ],
          countryCode: this.getInitialCountryCode(),
        },
        captcha: {
          value: null,
          validators: [],
        },
      },
      status: {
        submitting: false,
        submitted: false,
      },
      formTitle: i18nTranslate('Email agent'),
      error: '',
    };

    state.input.createAlert = {
      value: historyModel ? historyModel.createAlert : true,
      label: i18nTranslate('Email me similar properties'),
    };

    if (this.isCountryMorocco()) {
      state.input.createAlert.value = false;

      state.input.acceptTerms = {
        value: historyModel ? historyModel.acceptTerms : false,
        validators: [new InputValidatorRequired(i18nTranslate('Please accept the following conditions:'))],
      };

      state.input.receiveAdvertising = {
        value: historyModel ? historyModel.receiveAdvertising : false,
        validators: [new InputValidatorRequired(i18nTranslate('Please accept the following conditions:'))],
      };

      if (historyModel) {
        state.input.acceptTerms.value = historyModel.acceptTerms;
        state.input.receiveAdvertising.value = historyModel.receiveAdvertising;
      }
    }

    // Update email alert service
    this.updateEmailAlert();

    return state;
  }

  /**
   * @inheritDoc
   */
  public updateInput(input: LeadEmailFormInputInterface): void {
    const model = <LeadModelInterface>this.adapter.createModelFromLeadEmailForm(input);

    const historyModel = {
      name: model.name,
      email: model.email,
      createAlert: model.createAlert,
      phone: model.phone,
      countryCode: input.phone.countryCode,
      acceptTerms: false,
      receiveAdvertising: false,
    };

    if (this.isCountryMorocco()) {
      historyModel.acceptTerms = model.acceptTerms;
      historyModel.receiveAdvertising = model.receiveAdvertising;
    }

    this.browserStorageLocalService.setData(this.localStorageKey, historyModel);
  }

  /**
   * @inheritDoc
   */
  public setUserData(): void {
    const userModel = <UserModel>this.userAuthenticationService.getUser();
    const propertyLeadModel = this.browserStorageLocalService.getData(this.localStorageKey);
    let historyModel;

    if (!userModel) {
      return;
    }

    if (!propertyLeadModel) {
      historyModel = {
        name: userModel.first_name + ' ' + userModel.last_name,
        email: userModel.email,
        createAlert: false,
        phone: '',
        acceptTerms: false,
        receiveAdvertising: false,
      };
    } else {
      historyModel = propertyLeadModel;
    }

    this.browserStorageLocalService.setData(this.localStorageKey, historyModel);
  }

  /**
   * @inheritDoc
   */
  public getInitialCountryCode(): string {
    const propertyLeadModel = <LeadModelInterface>this.browserStorageLocalService.getData(this.localStorageKey);
    return (
      this.leadEmailFormContactService.getData(LeadEmailFormContactInputFieldNameEnum.countryCode) ||
      (propertyLeadModel && propertyLeadModel.countryCode ? propertyLeadModel.countryCode : configGetCountry().code)
    );
  }

  /**
   * Check if country is Morocco
   */
  private isCountryMorocco(): boolean {
    return configGetCountry().code === CountryCodeEnum.ma;
  }

  /**
   * Update email alert service
   */
  private updateEmailAlert(): void {
    const referrer = this.windowService.getNative().document.referrer;

    const hasValidReferrer = referrer && referrer.indexOf(this.windowService.getNative().location.host) !== -1;

    // Reset old params
    this.emailAlertService.setAdditionalParams({});

    if (!hasValidReferrer) {
      return;
    }

    // Get query params string from referer
    const queryParamsString = referrer.split('?')[1];

    if (!queryParamsString) {
      return;
    }

    const additionalParams = objectFilterFalsy(
      this.emailAlertAdditionalParamsAdapter.fromPropertySearchFormQueryParams(urlBuildQueryParams(queryParamsString))
    );

    // Set email alert additional params from referrer's query params
    this.emailAlertService.setAdditionalParams(additionalParams);
  }
}
